import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAllEncrypted, STORES } from '../utils/storage';
import { ArrowLeft, ChatCircle, Calendar, X } from 'phosphor-react';
import { toast } from 'sonner';

// Mirror Chat.js: strip markdown from assistant text so it renders cleanly.
const stripMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/(^|[\s(])\*([^*\n]+?)\*(?=[\s.,!?)]|$)/g, '$1$2')
    .replace(/(^|[\s(])_([^_\n]+?)_(?=[\s.,!?)]|$)/g, '$1$2')
    .replace(/`([^`\n]+)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[\s]*[-*+]\s+/gm, '')
    .replace(/^>\s+/gm, '');
};

const DiaryView = () => {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Full session object currently open in the detail panel. Null = list view.
  // Reuses the already-decrypted in-memory session; no extra IndexedDB read.
  const [selectedSession, setSelectedSession] = useState(null);
  const { encryptionKey } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadSessions();
  }, [encryptionKey]);

  // Esc closes the detail panel when it's open.
  useEffect(() => {
    if (!selectedSession) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedSession(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedSession]);

  const loadSessions = async () => {
    if (!encryptionKey) return;

    setIsLoading(true);
    try {
      const loadedSessions = await getAllEncrypted(STORES.SESSIONS, encryptionKey).catch(() => []);
      // Sort by startTime, newest first
      const sorted = loadedSessions.sort((a, b) => 
        new Date(b.startTime) - new Date(a.startTime)
      );
      setSessions(sorted);
    } catch (error) {
      console.error('Load sessions error:', error);
      toast.error('Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const openSession = (session) => {
    // Open inline panel with the already-decrypted session; no re-read.
    setSelectedSession(session);
  };

  const closeSession = () => {
    setSelectedSession(null);
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
  };

  const getToneColor = (tone) => {
    const toneColors = {
      calm: 'bg-emerald-400',
      mixed: 'bg-amber-400',
      distressed: 'bg-red-400',
      activated: 'bg-orange-400',
      reflective: 'bg-blue-400',
      hopeful: 'bg-green-400',
    };
    return toneColors[tone] || 'bg-stone-400';
  };

  const getSignificanceLabel = (significance) => {
    const labels = {
      low: 'Light',
      medium: 'Moderate',
      high: 'Significant',
    };
    return labels[significance] || 'Moderate';
  };

  return (
    <div className="min-h-screen bg-stone-50 noise-texture">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/chat')}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              data-testid="back-to-chat"
            >
              <ArrowLeft size={20} weight="duotone" />
            </button>
            <h1 className="font-serif font-semibold text-xl text-stone-800" data-testid="diary-title">
              Your Diary
            </h1>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-16 text-stone-500">Loading...</div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <ChatCircle size={32} weight="duotone" className="text-primary" />
            </div>
            <h2 className="text-2xl font-serif font-semibold text-stone-800 mb-2">
              Your journal is empty
            </h2>
            <p className="text-stone-600 mb-6">
              Start a conversation with Vela to create your first entry
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="inline-block bg-primary text-white rounded-full px-8 py-3 font-medium hover:shadow-soft transition-all"
              data-testid="start-first-entry"
            >
              Start Writing
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {sessions.map((session) => (
              <div
                key={session.id}
                onClick={() => openSession(session)}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-soft transition-all cursor-pointer border border-transparent hover:border-primary/20"
                data-testid={`session-${session.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm text-stone-500">
                    <Calendar size={16} weight="duotone" />
                    <span>{formatDate(session.startTime)}</span>
                    <span>•</span>
                    <span>{formatTime(session.startTime)}</span>
                  </div>
                  {session.messageCount && (
                    <span className="text-xs text-stone-400">
                      {session.messageCount} messages
                    </span>
                  )}
                </div>
                
                <h3 className="text-lg font-medium text-stone-800 mb-3 leading-relaxed">
                  {session.summary || session.messages?.[0]?.content?.substring(0, 60) + '...' || 'Untitled session'}
                </h3>

                <div className="flex items-center gap-3 flex-wrap">
                  {/* Tone indicator */}
                  {session.metadata?.tone && (
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${getToneColor(session.metadata.tone)}`} />
                      <span className="text-xs text-stone-500 capitalize">{session.metadata.tone}</span>
                    </div>
                  )}

                  {/* Significance badge */}
                  {session.metadata?.significance && session.metadata.significance !== 'low' && (
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      session.metadata.significance === 'high' 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'bg-stone-100 text-stone-600'
                    }`}>
                      {getSignificanceLabel(session.metadata.significance)}
                    </span>
                  )}

                  {/* Theme tags */}
                  {session.metadata?.themes && session.metadata.themes.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      {session.metadata.themes.slice(0, 3).map((theme, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary"
                        >
                          {theme}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Legacy intensity support (for older sessions) */}
                  {!session.metadata && session.intensity && (
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        session.intensity === 'high' ? 'bg-amber-400' :
                        session.intensity === 'medium' ? 'bg-primary/60' :
                        'bg-emerald-400'
                      }`} />
                      <span className="text-xs text-stone-500 capitalize">{session.intensity} intensity</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Inline Detail Panel — full-height overlay, mobile responsive.
          Reuses the already-decrypted session held in state. */}
      {selectedSession && (
        <div
          className="fixed inset-0 z-20 bg-stone-50 flex flex-col"
          data-testid="session-detail-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Saved diary entry"
        >
          {/* Panel Header */}
          <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
            <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
              <button
                onClick={closeSession}
                className="flex items-center gap-2 p-2 pr-3 hover:bg-stone-100 rounded-lg transition-colors text-stone-700"
                data-testid="back-to-diary-btn"
                aria-label="Back to Diary"
              >
                <ArrowLeft size={20} weight="duotone" />
                <span className="text-sm font-medium">Back to Diary</span>
              </button>
              <div className="flex-1 text-right overflow-hidden">
                <div className="text-xs text-stone-500 truncate">
                  {formatDate(selectedSession.startTime)} • {formatTime(selectedSession.startTime)}
                </div>
              </div>
              <button
                onClick={closeSession}
                className="p-2 hover:bg-stone-100 rounded-lg transition-colors text-stone-500 sm:hidden"
                data-testid="close-session-btn"
                aria-label="Close"
              >
                <X size={20} weight="duotone" />
              </button>
            </div>
          </header>

          {/* Panel Body — scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-4 py-6 pb-12">
              {/* Title + metadata */}
              <div className="mb-6">
                <h2
                  className="text-2xl font-serif font-semibold text-stone-800 mb-3 leading-snug"
                  data-testid="session-detail-title"
                >
                  {selectedSession.summary || 'Untitled session'}
                </h2>
                <div className="flex items-center gap-3 flex-wrap">
                  {selectedSession.metadata?.tone && (
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${getToneColor(selectedSession.metadata.tone)}`} />
                      <span className="text-xs text-stone-500 capitalize">{selectedSession.metadata.tone}</span>
                    </div>
                  )}
                  {selectedSession.metadata?.significance && selectedSession.metadata.significance !== 'low' && (
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      selectedSession.metadata.significance === 'high'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-stone-100 text-stone-600'
                    }`}>
                      {getSignificanceLabel(selectedSession.metadata.significance)}
                    </span>
                  )}
                  {selectedSession.metadata?.themes && selectedSession.metadata.themes.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      {selectedSession.metadata.themes.map((theme, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                          {theme}
                        </span>
                      ))}
                    </div>
                  )}
                  {selectedSession.messageCount != null && (
                    <span className="text-xs text-stone-400 ml-auto">
                      {selectedSession.messageCount} messages
                    </span>
                  )}
                </div>
              </div>

              {/* Conversation — chronological, full, user vs. Vela distinct */}
              {Array.isArray(selectedSession.messages) && selectedSession.messages.length > 0 ? (
                <div className="space-y-4" data-testid="session-detail-messages">
                  {selectedSession.messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`detail-message-${msg.role}-${idx}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-5 py-3 ${
                          msg.role === 'user'
                            ? 'bg-primary/10 text-stone-800'
                            : 'bg-secondary/10 text-stone-800'
                        }`}
                      >
                        <div className="text-[11px] font-medium uppercase tracking-wide text-stone-500 mb-1">
                          {msg.role === 'user' ? 'You' : 'Vela'}
                        </div>
                        <p className="whitespace-pre-wrap leading-relaxed">
                          {msg.role === 'assistant' ? stripMarkdown(msg.content) : msg.content}
                        </p>
                        {msg.timestamp && (
                          <span className="text-xs text-stone-400 mt-1 block">
                            {formatMessageTime(msg.timestamp)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Non-destructive error state — the entry still exists; we just
                // couldn't render its messages. No data is deleted.
                <div
                  className="bg-white rounded-2xl p-6 text-center border border-stone-200"
                  data-testid="session-detail-empty"
                >
                  <p className="text-stone-700 font-medium mb-2">
                    This entry's messages aren't available to display.
                  </p>
                  <p className="text-stone-500 text-sm">
                    Your saved entry is still here and untouched. You can return to the
                    diary and continue a new conversation with Vela anytime.
                  </p>
                  <button
                    onClick={closeSession}
                    className="mt-5 inline-block bg-primary text-white rounded-full px-6 py-2 text-sm font-medium hover:shadow-soft transition-all"
                    data-testid="detail-empty-back-btn"
                  >
                    Back to Diary
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiaryView;
